import { UserVocab } from "@/components/Types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type GamePhase = 'playing' | 'feedback' | 'completed';

export interface VocabGamesState {
    gameVocab: Partial<UserVocab>[];
    currentWordIndex: number;
    completedWords: string[]; // Array of word IDs that have been attempted
    correctCount: number;
    incorrectCount: number;
    gamePhase: GamePhase;
}

const initialState: VocabGamesState = {
    gameVocab: [],
    currentWordIndex: 0,
    completedWords: [],
    correctCount: 0,
    incorrectCount: 0,
    gamePhase: 'playing',
}

export const vocabGamesSlice = createSlice({
    name: 'vocabGames',
    initialState,
    reducers: {
        setGameVocab: (state, action: PayloadAction<Partial<UserVocab>[]>) => {
            state.gameVocab = action.payload;
            state.currentWordIndex = 0;
            state.completedWords = [];
            state.correctCount = 0;
            state.incorrectCount = 0;
            state.gamePhase = 'playing';
        },
        addGameVocab: (state, action: PayloadAction<Partial<UserVocab>>) => {
            state.gameVocab.push(action.payload);
        },
        removeGameVocab: (state, action: PayloadAction<Partial<UserVocab>>) => {
            state.gameVocab = state.gameVocab.filter(vocab => vocab._id !== action.payload._id);
        },
        clearGameVocab: (state) => {
            state.gameVocab = [];
            state.currentWordIndex = 0;
            state.completedWords = [];
            state.correctCount = 0;
            state.incorrectCount = 0;
            state.gamePhase = 'playing';
        },
        submitAnswer: (state, action: PayloadAction<{ isCorrect: boolean; wordId: string }>) => {
            const { isCorrect, wordId } = action.payload;
            if (isCorrect) {
                state.correctCount += 1;
            } else {
                state.incorrectCount += 1;
            }
            if (!state.completedWords.includes(wordId)) {
                state.completedWords.push(wordId);
            }
            state.gamePhase = 'feedback';
        },
        nextWord: (state) => {
            if (state.currentWordIndex < state.gameVocab.length - 1) {
                state.currentWordIndex += 1;
                state.gamePhase = 'playing';
            } else {
                state.gamePhase = 'completed';
            }
        },
        resetGame: (state) => {
            state.currentWordIndex = 0;
            state.completedWords = [];
            state.correctCount = 0;
            state.incorrectCount = 0;
            state.gamePhase = 'playing';
        },
        setGamePhase: (state, action: PayloadAction<GamePhase>) => {
            state.gamePhase = action.payload;
        },
    }
});

export const { 
    setGameVocab, 
    addGameVocab, 
    removeGameVocab, 
    clearGameVocab,
    submitAnswer,
    nextWord,
    resetGame,
    setGamePhase,
} = vocabGamesSlice.actions;
export default vocabGamesSlice.reducer;